import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Link2Off,
  UserRound,
  Users,
} from "lucide-react";
import "../Services/styles/ServiceEditPage.css";
import "./styles/EmailEditPage.css";
import showToast from "../../utils/toast";
import { ServiceEmailRequest } from "../../models/ServiceEmailRequestModel";
import {
  ServiceEmailSolicitud,
  ServiceEmailSolicitudDefaults,
  ServiceEmailSolicitudPayload,
  ServiceEmailSolicitudServiceLink,
  ServiceEmailSolicitudServicesPayload,
} from "../../models/ServiceEmailSolicitudModel";
import useServiceEmailSolicitudesService from "../../services/useServiceEmailSolicitudesService";
import useServiceEmailsService from "../../services/useServiceEmailsService";
import useCompaniesService from "../../services/useCompaniesService";
import ConfirmAlert from "../../components/Base/ConfirmAlert";
import ServiceEmailSolicitudModal from "../../components/Services/forms/ServiceEmailSolicitudModal";
import ServiceEmailSolicitudServicesModal from "../../components/Services/forms/ServiceEmailSolicitudServicesModal";
import ServiceEmailCommentModal from "../../components/Services/forms/ServiceEmailCommentModal";
import { ServiceEmailNote } from "../../models/ServiceEmailNoteModel";

const formatDate = (raw?: string | null) => {
  if (!raw) return "—";
  const [year, month, day] = raw.slice(0, 10).split("-");
  if (!year || !month || !day) return raw;
  return `${day}-${month}-${year.slice(-2)}`;
};

const formatTime = (raw?: string | null) => {
  if (!raw) return "—";
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat("es-CL", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }
  const match = String(raw).match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : raw;
};

const formatNoteDate = (iso: string) => {
  try {
    const date = new Date(iso);
    return new Intl.DateTimeFormat("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(date);
  } catch {
    return iso;
  }
};

const parseServiceIds = (raw?: string | null) =>
  String(raw ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => /^\d+$/.test(part));

const serviceStatusTone = (status?: string | null) => {
  const normalized = String(status ?? "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (normalized.includes("COMPLETADO")) return "completado";
  if (normalized.includes("EN_CURSO") || normalized.includes("EN CURSO")) return "en-curso";
  if (normalized.includes("ASIGNADO")) return "asignado";
  if (normalized.includes("AUTORIZADO")) return "autorizado";
  if (normalized.includes("PROGRAMADO")) return "programado";
  if (normalized.includes("PREPARACION")) return "preparacion";
  if (normalized.includes("ANULADO") || normalized.includes("CANCELADO")) return "anulado";
  return "default";
};

const ServiceIdLinks: React.FC<{
  ids: string[];
  onNavigate: (id: string) => void;
}> = ({ ids, onNavigate }) => {
  if (ids.length === 0) return <span className="email-edit-empty-cell">—</span>;
  return (
    <div className="email-edit-service-links">
      {ids.map((serviceId) => (
        <button
          key={serviceId}
          type="button"
          className="email-edit-service-link"
          onClick={() => onNavigate(serviceId)}
          title={`Ir al servicio #${serviceId}`}
        >
          #{serviceId}
        </button>
      ))}
    </div>
  );
};

const EmailEditPage: React.FC = () => {
  const { id = "0" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const emailId = Number(id);
  const { list: listCompanies } = useCompaniesService();
  const { updateTags, updateCompany } = useServiceEmailsService();
  const solicitudSvc = useServiceEmailSolicitudesService();
  const {
    detail,
    store: storeSolicitud,
    update: updateSolicitud,
    remove: removeSolicitud,
    storeServices,
    removeServiceLink,
  } = solicitudSvc;

  const [email, setEmail] = useState<ServiceEmailRequest | null>(
    (location.state as { email?: ServiceEmailRequest } | null)?.email ?? null
  );
  const [solicitudes, setSolicitudes] = useState<ServiceEmailSolicitud[]>([]);
  const [notes, setNotes] = useState<ServiceEmailNote[]>([]);
  const [defaults, setDefaults] = useState<ServiceEmailSolicitudDefaults | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [companyOptions, setCompanyOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [savingCompany, setSavingCompany] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSolicitudModal, setShowSolicitudModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [editingSolicitud, setEditingSolicitud] = useState<ServiceEmailSolicitud | null>(null);
  const [linkingSolicitud, setLinkingSolicitud] = useState<ServiceEmailSolicitud | null>(null);
  const [linkToUnlink, setLinkToUnlink] = useState<ServiceEmailSolicitudServiceLink | null>(null);
  const [solicitudToDelete, setSolicitudToDelete] = useState<ServiceEmailSolicitud | null>(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [expandedLinkedIds, setExpandedLinkedIds] = useState<number[]>([]);

  const loadDetail = useCallback(() => {
    if (!emailId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    detail(emailId, {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        const loadedEmail = data.email ?? null;
        setEmail(loadedEmail);
        setSolicitudes(Array.isArray(data.solicitudes) ? data.solicitudes : []);
        setNotes(Array.isArray(data.notes) ? data.notes : []);
        setDefaults({
          passenger_count: data.email?.passenger_count ?? 1,
          passenger_names: "",
          request_date: data.email?.request_date ?? null,
          request_time: data.email?.request_time ?? null,
          origin_text: data.email?.origin_text ?? "",
          destination_text: data.email?.destination_text ?? "",
        });
        setLoading(false);
        if (!data.email) {
          showToast("No se encontró el correo", "error");
        }
      },
      onError: () => {
        setLoading(false);
        showToast("Error cargando el correo", "error");
      },
    });
  }, [detail, emailId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    listCompanies(
      {},
      {
        onSuccess: (resp: any) => {
          const items = Array.isArray(resp?.data?.companies) ? resp.data.companies : [];
          setCompanyOptions(
            items.map((c: any) => ({
              value: String(c.id),
              label: c.name ?? c.company_name ?? "Sin nombre",
            }))
          );
        },
      }
    );
  }, [listCompanies]);

  const selectedCompanyId =
    email?.company != null
      ? String(email.company)
      : email?.sender_company_id != null
        ? String(email.sender_company_id)
        : "";

  useEffect(() => {
    if (email?.company_name) {
      setCompanyName(email.company_name);
      return;
    }
    if (!selectedCompanyId) {
      setCompanyName("");
      return;
    }
    const company = companyOptions.find((c) => c.value === selectedCompanyId);
    setCompanyName(company?.label ?? "");
  }, [email?.company_name, selectedCompanyId, companyOptions]);

  const handleCompanyChange = async (value: string) => {
    if (!email?.id || savingCompany) return;
    const companyId = value ? Number(value) : null;
    const previous = email.company ?? null;
    setEmail((prev) => (prev ? { ...prev, company: companyId } : prev));
    setSavingCompany(true);
    await updateCompany(
      email.id,
      { company: companyId },
      {
        onSuccess: (resp: any) => {
          const updated = resp?.data;
          setEmail((prev) =>
            prev
              ? {
                  ...prev,
                  company: updated?.company ?? companyId,
                  company_name: updated?.company_name ?? null,
                }
              : prev
          );
          setSavingCompany(false);
        },
        onError: () => {
          setEmail((prev) => (prev ? { ...prev, company: previous } : prev));
          setSavingCompany(false);
          showToast("No se pudo actualizar la empresa", "error");
        },
      }
    );
  };

  const serviceRows = useMemo(() => {
    const rows: Array<ServiceEmailSolicitudServiceLink & { solicitud_id: number }> = [];
    solicitudes.forEach((solicitud) => {
      (solicitud.service_links ?? []).forEach((link) => {
        rows.push({ ...link, solicitud_id: solicitud.id });
      });
    });
    return rows;
  }, [solicitudes]);

  const linkedServiceIds = useMemo(
    () =>
      Array.from(new Set(
        serviceRows
          .map((row) => String(row.service_id ?? row.service ?? ""))
          .filter((serviceId) => /^\d+$/.test(serviceId))
      )),
    [serviceRows]
  );

  const metadata = useMemo(() => {
    if (!email) {
      return {
        empresa: "—",
        emisor: "—",
        diaRecibido: "—",
        horaRecibido: "—",
        estado: "PENDIENTE",
        statusLabel: "Pendiente",
      };
    }

    return {
      empresa: companyName || "—",
      emisor: email.sender_email || "—",
      diaRecibido: formatDate(email.received_at),
      horaRecibido: formatTime(email.received_at),
      estado: email.status === "AGREGADO" ? "AGREGADO" : "PENDIENTE",
      statusLabel: email.status_label ?? (email.status === "AGREGADO" ? "Agregado" : "Pendiente"),
    };
  }, [companyName, email]);

  const openCreateSolicitud = () => {
    setEditingSolicitud(null);
    setShowSolicitudModal(true);
  };

  const openEditSolicitud = (solicitud: ServiceEmailSolicitud) => {
    setEditingSolicitud(solicitud);
    setShowSolicitudModal(true);
  };

  const openLinkServices = (solicitud: ServiceEmailSolicitud) => {
    if ((solicitud.remaining_passenger_count ?? solicitud.passenger_count) <= 0) {
      showToast("Esta solicitud ya tiene todos los pasajeros asignados", "info");
      return;
    }
    setLinkingSolicitud(solicitud);
    setShowServicesModal(true);
  };

  const handleSolicitudSubmit = async (payload: ServiceEmailSolicitudPayload, item?: ServiceEmailSolicitud | null) => {
    if (item?.id) {
      await updateSolicitud(item.id, payload, {
        onSuccess: () => {
          showToast("Solicitud actualizada", "success");
          setShowSolicitudModal(false);
          setEditingSolicitud(null);
          loadDetail();
        },
        onError: () => showToast("Error actualizando solicitud", "error"),
      });
      return;
    }

    await storeSolicitud(emailId, payload, {
      onSuccess: () => {
        showToast("Solicitud creada", "success");
        setShowSolicitudModal(false);
        loadDetail();
      },
      onError: () => showToast("Error creando solicitud", "error"),
    });
  };

  const handleServicesSubmit = async (payload: ServiceEmailSolicitudServicesPayload) => {
    if (!linkingSolicitud?.id) return;

    await storeServices(linkingSolicitud.id, payload, {
      onSuccess: () => {
        showToast("Servicios vinculados", "success");
        setShowServicesModal(false);
        setLinkingSolicitud(null);
        loadDetail();
      },
      onError: (resp: any) => {
        const message =
          resp?.data?.items?.[0] ||
          resp?.message ||
          "Error vinculando servicios. Verifica los IDs y la cantidad de pasajeros.";
        showToast(String(message), "error");
      },
    });
  };

  const confirmUnlinkService = async () => {
    if (!linkToUnlink) return;

    await removeServiceLink(linkToUnlink.id, {
      onSuccess: () => {
        showToast("Servicio desvinculado", "success");
        loadDetail();
      },
      onError: () => showToast("Error desvinculando el servicio", "error"),
    });
  };

  const confirmDeleteSolicitud = async () => {
    if (!solicitudToDelete) return;

    await removeSolicitud(solicitudToDelete.id, {
      onSuccess: () => {
        showToast("Solicitud eliminada", "success");
        loadDetail();
      },
      onError: (resp: any) =>
        showToast(resp?.message || "No se pudo eliminar la solicitud", "error"),
    });
  };

  const handleTagToggle = async (
    field: "tag_planilla" | "tag_plataforma" | "tag_agendando_sistema",
    checked: boolean
  ) => {
    if (!email?.id || savingTags) return;
    const previous = {
      tag_planilla: Boolean(email.tag_planilla),
      tag_plataforma: Boolean(email.tag_plataforma),
      tag_agendando_sistema: Boolean(email.tag_agendando_sistema),
    };
    setEmail((prev) => (prev ? { ...prev, [field]: checked } : prev));
    setSavingTags(true);
    await updateTags(
      email.id,
      { [field]: checked },
      {
        onSuccess: (resp: any) => {
          const updated = resp?.data;
          if (updated) {
            setEmail((prev) =>
              prev
                ? {
                    ...prev,
                    tag_planilla: Boolean(updated.tag_planilla),
                    tag_plataforma: Boolean(updated.tag_plataforma),
                    tag_agendando_sistema: Boolean(updated.tag_agendando_sistema),
                  }
                : prev
            );
          }
          setSavingTags(false);
        },
        onError: () => {
          setEmail((prev) => (prev ? { ...prev, ...previous } : prev));
          setSavingTags(false);
          showToast("No se pudo actualizar la etiqueta", "error");
        },
      }
    );
  };

  if (loading) {
    return (
      <div className="sp-edit-pro">
        <div className="service-edit-header-pro">
          <div className="header-top-pro">
            <button className="btn-back-pro" type="button" onClick={() => navigate(-1)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                <path
                  d="M12.5 15L7.5 10L12.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Volver</span>
            </button>
            <div className="header-center-pro">
              <h1 className="service-title-pro">Cargando correo…</h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="sp-edit-pro">
        <div className="service-edit-header-pro">
          <div className="header-top-pro">
            <button className="btn-back-pro" type="button" onClick={() => navigate("/emails")}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                <path
                  d="M12.5 15L7.5 10L12.5 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Volver</span>
            </button>
            <div className="header-center-pro">
              <h1 className="service-title-pro">Correo no encontrado</h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subject = email.subject?.trim() || "Sin asunto";

  return (
    <div className="sp-edit-pro email-edit-page">
      <div className="service-edit-header-pro">
        <div className="header-top-pro">
          <button className="btn-back-pro" type="button" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Volver</span>
          </button>

          <div className="header-center-pro">
            <h1 className="service-title-pro">Correo #{email.id}</h1>
            <p className="email-edit-header-subject" title={subject}>
              {subject}
            </p>
          </div>

          <div className="header-actions-pro">
            <span className={`status-badge-pro status-${email.status.toLowerCase()}`}>
              {metadata.statusLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="email-edit-content">
        <section className="email-edit-top">
          <div className="email-edit-panel">
            <div className="email-edit-panel-head">
              <h3 className="email-edit-section-title">Correo</h3>
              <span className={`status-badge-pro status-${email.status.toLowerCase()}`}>
                {metadata.statusLabel}
              </span>
            </div>
            <div className="email-edit-panel-body">
              <div className="email-edit-meta-grid">
                <div className="email-edit-meta-item">
                  <span className="email-edit-meta-label">Empresa</span>
                  <select
                    className="email-edit-meta-select"
                    value={selectedCompanyId}
                    disabled={savingCompany}
                    onChange={(event) => handleCompanyChange(event.target.value)}
                  >
                    <option value="">Sin empresa</option>
                    {companyOptions.map((company) => (
                      <option key={company.value} value={company.value}>
                        {company.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="email-edit-meta-item">
                  <span className="email-edit-meta-label">Emisor</span>
                  <span className="email-edit-meta-value">{metadata.emisor}</span>
                </div>
                <div className="email-edit-meta-item">
                  <span className="email-edit-meta-label">Día recibido</span>
                  <span className="email-edit-meta-value">{metadata.diaRecibido}</span>
                </div>
                <div className="email-edit-meta-item">
                  <span className="email-edit-meta-label">Hora recibido</span>
                  <span className="email-edit-meta-value">{metadata.horaRecibido}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="email-edit-panel">
            <div className="email-edit-panel-head">
              <h3 className="email-edit-section-title">Resumen</h3>
            </div>
            <div className="email-edit-panel-body">
              <div className="email-edit-stats">
                <div className="email-edit-stat-card email-edit-stat-card--primary">
                  <span className="email-edit-stat-value">{solicitudes.length}</span>
                  <span className="email-edit-stat-label">Solicitudes</span>
                </div>
                <div className="email-edit-stat-card email-edit-stat-card--success">
                  <span className="email-edit-stat-value">{serviceRows.length}</span>
                  <span className="email-edit-stat-label">Servicios</span>
                </div>
              </div>
              <div className="email-edit-summary-services">
                <span className="email-edit-stat-label">Servicios agregados</span>
                <ServiceIdLinks
                  ids={linkedServiceIds}
                  onNavigate={(serviceId) => navigate(`/services/edit/${serviceId}`)}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="email-edit-panel email-edit-tags-panel">
          <div className="email-edit-panel-head">
            <h3 className="email-edit-section-title">Etiquetas</h3>
            {savingTags && <span className="email-edit-tags-saving">Guardando…</span>}
          </div>
          <div className="email-edit-panel-body">
            <div className="email-edit-tags-checklist" role="group" aria-label="Checklist de etiquetas">
              <label className={`email-edit-tag-check${email.tag_planilla ? " is-checked" : ""}`}>
                <input
                  type="checkbox"
                  checked={Boolean(email.tag_planilla)}
                  disabled={savingTags}
                  onChange={(event) => handleTagToggle("tag_planilla", event.target.checked)}
                />
                <span>Planilla</span>
              </label>
              <label className={`email-edit-tag-check${email.tag_plataforma ? " is-checked" : ""}`}>
                <input
                  type="checkbox"
                  checked={Boolean(email.tag_plataforma)}
                  disabled={savingTags}
                  onChange={(event) => handleTagToggle("tag_plataforma", event.target.checked)}
                />
                <span>Plataforma</span>
              </label>
              <label
                className={`email-edit-tag-check${email.tag_agendando_sistema ? " is-checked" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={Boolean(email.tag_agendando_sistema)}
                  disabled={savingTags}
                  onChange={(event) =>
                    handleTagToggle("tag_agendando_sistema", event.target.checked)
                  }
                />
                <span>Agendando por sistema</span>
              </label>
            </div>
          </div>
        </section>

        <section className="email-edit-panel email-edit-comment-panel">
          <div className="email-edit-panel-head">
            <div className="email-edit-notes-head">
              <h3 className="email-edit-section-title">Comentarios</h3>
              <span className="email-edit-notes-count">
                {notes.length} comentario{notes.length !== 1 ? "s" : ""}
              </span>
            </div>
            <button
              type="button"
              className="email-edit-comment-btn"
              onClick={() => setShowCommentModal(true)}
            >
              + Agregar
            </button>
          </div>
          <div className="email-edit-panel-body">
            {notes.length === 0 ? (
              <p className="email-edit-comment-empty">No hay comentarios aún. Añade el primero.</p>
            ) : (
              <div className="email-edit-notes-grid">
                {notes.map((note, index) => {
                  const author = note.user_full_name || "Sistema";
                  const initial = author.trim().charAt(0).toUpperCase() || "N";
                  return (
                    <article
                      key={note.id}
                      className="email-edit-note-card"
                      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
                    >
                      <div className="email-edit-note-card__accent" aria-hidden />
                      <div className="email-edit-note-card-meta">
                        <div className="email-edit-note-card-author">
                          <span className="email-edit-note-card-avatar" aria-hidden>
                            {initial}
                          </span>
                          <span className="email-edit-note-card-user">{author}</span>
                        </div>
                        <span className="email-edit-note-card-date">
                          {formatNoteDate(note.created_at)}
                        </span>
                      </div>
                      <p className="email-edit-note-card-text">{note.text}</p>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="email-edit-panel email-edit-table-section">
          <div className="email-edit-panel-head">
            <div className="email-edit-section-toolbar">
              <h3 className="email-edit-section-title">Solicitudes</h3>
              <button type="button" className="email-edit-btn-add" onClick={openCreateSolicitud}>
                + Nueva solicitud
              </button>
            </div>
          </div>
          <div className="email-edit-table-wrap">
            <table className="email-edit-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Servicios</th>
                  <th>Total</th>
                  <th>Asign.</th>
                  <th>Rest.</th>
                  <th>Pax</th>
                  <th>Hora</th>
                  <th>Día</th>
                  <th>Origen → Destino</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {solicitudes.length === 0 ? (
                  <tr className="email-edit-empty-row">
                    <td colSpan={10}>Sin solicitudes. Crea una desde el correo base.</td>
                  </tr>
                ) : (
                  solicitudes.map((row) => {
                    const linkedServiceIds = parseServiceIds(row.service_ids);
                    const remaining = row.remaining_passenger_count ?? 0;
                    const hasLinkedServices = (row.service_links?.length ?? 0) > 0;
                    return (
                      <tr key={row.id}>
                        <td className="col-id">{row.id}</td>
                        <td>
                          <ServiceIdLinks
                            ids={linkedServiceIds}
                            onNavigate={(serviceId) => navigate(`/services/edit/${serviceId}`)}
                          />
                        </td>
                        <td className="col-num">
                          <span className="email-edit-pax-badge email-edit-pax-badge--total">
                            {row.passenger_count}
                          </span>
                        </td>
                        <td className="col-num">
                          <span className="email-edit-pax-badge email-edit-pax-badge--assigned">
                            {row.assigned_passenger_count ?? 0}
                          </span>
                        </td>
                        <td className="col-num">
                          <span
                            className={`email-edit-pax-badge ${
                              remaining > 0 ? "email-edit-pax-badge--remaining" : "email-edit-pax-badge--done"
                            }`}
                          >
                            {remaining}
                          </span>
                        </td>
                        <td className="email-edit-empty-cell">{row.passenger_names || "—"}</td>
                        <td>{formatTime(row.request_time)}</td>
                        <td>{formatDate(row.request_date)}</td>
                        <td>
                          {row.origin_text || row.destination_text ? (
                            <span className="email-edit-route" title={`${row.origin_text} → ${row.destination_text}`}>
                              {row.origin_text || "—"}
                              <span className="email-edit-route-arrow">→</span>
                              {row.destination_text || "—"}
                            </span>
                          ) : (
                            <span className="email-edit-empty-cell">—</span>
                          )}
                        </td>
                        <td>
                          <div className="email-edit-row-actions">
                            <button
                              type="button"
                              className="email-edit-btn-sm"
                              onClick={() => openEditSolicitud(row)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="email-edit-btn-sm email-edit-btn-sm--primary"
                              onClick={() => openLinkServices(row)}
                              disabled={remaining <= 0}
                            >
                              + Servicios
                            </button>
                            {!hasLinkedServices && (
                              <button
                                type="button"
                                className="email-edit-btn-sm email-edit-btn-sm--danger"
                                onClick={() => setSolicitudToDelete(row)}
                                disabled={Boolean(solicitudSvc.fetching.remove)}
                              >
                                Eliminar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="email-edit-panel email-edit-table-section">
          <div className="email-edit-panel-head">
            <h3 className="email-edit-section-title">Servicios vinculados</h3>
            {serviceRows.length > 0 && (
              <span className="email-edit-linked-count">{serviceRows.length}</span>
            )}
          </div>

          {serviceRows.length === 0 ? (
            <div className="email-edit-linked-empty">Sin servicios vinculados.</div>
          ) : (
            <div className="email-edit-linked-cards">
              {serviceRows.map((row) => {
                const serviceId = row.service_id ?? row.service;
                const statusRaw = row.service_status || row.service_status_label || "";
                const statusLabel = row.service_status_label || row.service_status || "Sin estado";
                const tone = serviceStatusTone(statusRaw || statusLabel);
                const serviceDate = row.service_date || row.request_date;
                const serviceTime = row.service_time || row.request_time;
                const origin =
                  (row.service_origin_name || "").trim() || (row.origin_text || "").trim();
                const destination =
                  (row.service_destination_name || "").trim() ||
                  (row.destination_text || "").trim();
                const passengerDetails = Array.isArray(row.passenger_details)
                  ? row.passenger_details
                  : [];
                const passengersSummary = (
                  row.service_passenger_names ||
                  row.passenger_names ||
                  ""
                ).trim();
                const paxCount =
                  passengerDetails.length > 0
                    ? passengerDetails.length
                    : row.passenger_count;
                const isExpanded = expandedLinkedIds.includes(row.id);
                const serviceNotes = Array.isArray(row.service_notes) ? row.service_notes : [];
                const elAbra = row.el_abra || null;
                const costCenter =
                  (row.cost_center_code || "").trim() ||
                  (row.cost_center_name || "").trim();
                const enaexCode = (row.enaex_code || "").trim();
                const description = (row.description || "").trim();

                const toggleDetail = () => {
                  setExpandedLinkedIds((prev) =>
                    prev.includes(row.id)
                      ? prev.filter((id) => id !== row.id)
                      : [...prev, row.id]
                  );
                };

                return (
                  <article
                    key={row.id}
                    className={`email-edit-linked-card email-edit-linked-card--${tone}${
                      isExpanded ? " is-expanded" : ""
                    }`}
                  >
                    <div className="email-edit-linked-card__accent" aria-hidden />

                    <div className="email-edit-linked-card__main">
                      <header className="email-edit-linked-card__header">
                        <div className="email-edit-linked-card__title-block">
                          {serviceId ? (
                            <button
                              type="button"
                              className="email-edit-linked-card__id"
                              onClick={() => navigate(`/services/edit/${serviceId}`)}
                              title={`Ir al servicio #${serviceId}`}
                            >
                              #{serviceId}
                            </button>
                          ) : (
                            <span className="email-edit-linked-card__id is-empty">Sin ID</span>
                          )}
                          <div className="email-edit-linked-card__chips">
                            <span className="email-edit-linked-chip">
                              Solic. #{row.solicitud_id}
                            </span>
                            <span className="email-edit-linked-chip">
                              <Calendar size={12} strokeWidth={2.25} aria-hidden />
                              {formatDate(serviceDate)}
                            </span>
                            <span className="email-edit-linked-chip">
                              <Users size={12} strokeWidth={2.25} aria-hidden />
                              {paxCount} pax
                            </span>
                            {enaexCode ? (
                              <span className="email-edit-linked-chip email-edit-linked-chip--code">
                                ENAEX {enaexCode}
                              </span>
                            ) : null}
                          </div>
                        </div>

                        <div className="email-edit-linked-card__time" title="Fecha y hora del servicio">
                          <div className="email-edit-linked-card__time-date">
                            <Calendar size={13} strokeWidth={2.25} aria-hidden />
                            <span>{formatDate(serviceDate)}</span>
                          </div>
                          <div className="email-edit-linked-card__time-hour">
                            <Clock size={15} strokeWidth={2.25} aria-hidden />
                            <strong>{formatTime(serviceTime)}</strong>
                          </div>
                        </div>
                      </header>

                      <div className="email-edit-linked-card__route">
                        <div className="email-edit-linked-card__place">
                          <small>Origen</small>
                          <strong title={origin || undefined}>{origin || "—"}</strong>
                        </div>
                        <div className="email-edit-linked-card__route-mid" aria-hidden>
                          <span className="email-edit-linked-card__route-line" />
                          <ArrowRight size={15} strokeWidth={2.25} />
                          <span className="email-edit-linked-card__route-line" />
                        </div>
                        <div className="email-edit-linked-card__place email-edit-linked-card__place--end">
                          <small>Destino</small>
                          <strong title={destination || undefined}>{destination || "—"}</strong>
                        </div>
                      </div>

                      {(passengerDetails.length > 0 || passengersSummary) && (
                        <div className="email-edit-linked-card__passengers">
                          <div className="email-edit-linked-card__passengers-title">
                            <Users size={13} strokeWidth={2.25} aria-hidden />
                            Pasajeros
                            {passengerDetails.length > 0 ? ` (${passengerDetails.length})` : ""}
                          </div>
                          {passengerDetails.length > 0 ? (
                            <ul className="email-edit-linked-card__passengers-list">
                              {passengerDetails.map((pax, index) => (
                                <li key={pax.id ?? `${pax.name}-${index}`}>
                                  <strong>{pax.name || "Pasajero sin nombre"}</strong>
                                  {pax.address ? <span>{pax.address}</span> : null}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="email-edit-linked-card__passengers-summary">
                              {passengersSummary}
                            </p>
                          )}
                        </div>
                      )}

                      {isExpanded && (
                        <div className="email-edit-linked-card__details">
                          <div className="email-edit-linked-details-grid">
                            <div className="email-edit-linked-detail">
                              <small>Centro de costo</small>
                              <strong>{costCenter || "—"}</strong>
                            </div>
                            <div className="email-edit-linked-detail">
                              <small>Autorizador</small>
                              <strong>{row.authorizer_name || "—"}</strong>
                            </div>
                            <div className="email-edit-linked-detail">
                              <small>Solicitante</small>
                              <strong>{row.requester_name || "—"}</strong>
                            </div>
                            <div className="email-edit-linked-detail">
                              <small>Tipo</small>
                              <strong>{row.service_type_label || row.service_type || "—"}</strong>
                            </div>
                            {enaexCode ? (
                              <div className="email-edit-linked-detail">
                                <small>Código ENAEX</small>
                                <strong>{enaexCode}</strong>
                              </div>
                            ) : null}
                            {elAbra?.management ? (
                              <div className="email-edit-linked-detail">
                                <small>Gerencia</small>
                                <strong>{elAbra.management}</strong>
                              </div>
                            ) : null}
                            {elAbra?.superintendency ? (
                              <div className="email-edit-linked-detail">
                                <small>Superintendencia</small>
                                <strong>{elAbra.superintendency}</strong>
                              </div>
                            ) : null}
                            {elAbra?.area ? (
                              <div className="email-edit-linked-detail">
                                <small>Área</small>
                                <strong>{elAbra.area}</strong>
                              </div>
                            ) : null}
                            {(elAbra?.service_reason || elAbra?.service_reason_text) ? (
                              <div className="email-edit-linked-detail email-edit-linked-detail--wide">
                                <small>Motivo El Abra</small>
                                <strong>
                                  {elAbra.service_reason_text || elAbra.service_reason || "—"}
                                </strong>
                              </div>
                            ) : null}
                            {description ? (
                              <div className="email-edit-linked-detail email-edit-linked-detail--wide">
                                <small>Descripción</small>
                                <strong>{description}</strong>
                              </div>
                            ) : null}
                          </div>

                          {serviceNotes.length > 0 ? (
                            <div className="email-edit-linked-notes">
                              <div className="email-edit-linked-card__passengers-title">
                                Notas del servicio ({serviceNotes.length})
                              </div>
                              <ul>
                                {serviceNotes.map((note, noteIndex) => (
                                  <li
                                    key={note.id ?? note.created_at ?? note.text}
                                    style={{
                                      animationDelay: `${Math.min(noteIndex, 6) * 40}ms`,
                                    }}
                                  >
                                    <p>{note.text || "—"}</p>
                                    <span>
                                      {[note.user_name, note.created_at ? formatNoteDate(note.created_at) : ""]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="email-edit-linked-notes-empty">Sin notas en el servicio.</p>
                          )}
                        </div>
                      )}

                      <footer className="email-edit-linked-card__footer">
                        <div className="email-edit-linked-card__driver">
                          <span className="email-edit-linked-card__avatar" aria-hidden>
                            <UserRound size={14} strokeWidth={2.25} />
                          </span>
                          <div>
                            <small>Conductor</small>
                            <strong>{row.driver_name || "Sin asignar"}</strong>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="email-edit-linked-card__detail-btn"
                          onClick={toggleDetail}
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <ChevronUp size={14} strokeWidth={2.25} aria-hidden />
                          ) : (
                            <ChevronDown size={14} strokeWidth={2.25} aria-hidden />
                          )}
                          {isExpanded ? "Ocultar detalle" : "Ver detalle"}
                        </button>

                        <button
                          type="button"
                          className="email-edit-linked-card__unlink"
                          onClick={() => setLinkToUnlink(row)}
                          disabled={Boolean(solicitudSvc.fetching.removeServiceLink)}
                          title="Desvincular servicio"
                        >
                          <Link2Off size={14} strokeWidth={2} aria-hidden />
                          Desvincular
                        </button>
                      </footer>
                    </div>

                    <aside
                      className={`email-edit-linked-card__status email-edit-linked-card__status--${tone}`}
                      aria-label={`Estado: ${statusLabel}`}
                    >
                      <span className="email-edit-linked-card__status-dot" aria-hidden />
                      <small>Estado</small>
                      <strong>{statusLabel}</strong>
                    </aside>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <ServiceEmailSolicitudModal
        isOpen={showSolicitudModal}
        item={editingSolicitud}
        defaults={defaults}
        loading={Boolean(solicitudSvc.fetching.store || solicitudSvc.fetching.update)}
        onClose={() => {
          setShowSolicitudModal(false);
          setEditingSolicitud(null);
        }}
        onSubmit={handleSolicitudSubmit}
      />

      <ServiceEmailSolicitudServicesModal
        isOpen={showServicesModal}
        solicitud={linkingSolicitud}
        companyId={selectedCompanyId ? Number(selectedCompanyId) : null}
        companyName={companyName}
        loading={Boolean(solicitudSvc.fetching.storeServices)}
        onClose={() => {
          setShowServicesModal(false);
          setLinkingSolicitud(null);
        }}
        onSubmit={handleServicesSubmit}
      />

      <ConfirmAlert
        isOpen={Boolean(linkToUnlink)}
        title="Desvincular servicio"
        message={`¿Desvincular el servicio #${linkToUnlink?.service_id ?? linkToUnlink?.service ?? "—"} de esta solicitud?`}
        onConfirm={confirmUnlinkService}
        onClose={() => setLinkToUnlink(null)}
      />
      <ConfirmAlert
        isOpen={Boolean(solicitudToDelete)}
        title="Eliminar solicitud"
        message={`¿Eliminar la solicitud #${solicitudToDelete?.id}? Esta acción no se puede deshacer.`}
        onConfirm={confirmDeleteSolicitud}
        onClose={() => setSolicitudToDelete(null)}
      />

      <ServiceEmailCommentModal
        isOpen={showCommentModal}
        onClose={() => setShowCommentModal(false)}
        emailId={email.id}
        subject={email.subject}
        onSuccess={loadDetail}
      />
    </div>
  );
};

export default EmailEditPage;
